import {
  Button,
  Card,
  ChevronRightIcon,
  CircleAlertIcon,
  ClockIcon,
  DirhamIcon,
  PaymentHistoryIcon,
  Skeleton,
  StatusChip,
  Typography,
} from "@/components/atoms";
import { PaymentCardSkeleton } from "@/components/molecules";
import WebAppLayout from "@/components/organisms/WebAppLayout";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { resolveGreetingPrefix, withGreetingNameComma } from "@/core/utils/greeting";
import { isSmallScreen } from "@/core/utils/responsive";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";
import React from "react";
import {
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";

interface HomeScreenWebProps {
  // Data
  userName: string;
  userEmail: string;
  totalProperties: number;
  totalPurchasePrice: number;
  overduePaymentCards: {
    id: string;
    propertyName: string;
    amount: number;
    paymentCount: number;
  }[];
  upcomingDuePaymentCards: {
    id: string;
    propertyName: string;
    amount: number;
    paymentCount: number;
  }[];
  paymentUpdates: {
    id: string;
    propertyName: string;
    paymentTitle: string;
    amount: number;
    status: "in-process" | "confirmed";
    paymentDate: string;
  }[];
  homeCoverImage: string | null;
  ventures: { id: string; logoUrl: string }[];
  // Loading states
  profileLoading: boolean;
  unitInstallmentsLoading: boolean;
  // Handlers
  onPaymentCardPress: (
    propertyId: string,
    category: "overdue" | "upcomingDue",
  ) => void;
  onPaymentUpdatePress: (paymentId: string) => void;
  onVenturePress: (id: string) => void;
  onViewAllMissed: () => void;
  onViewAllDueSoon: () => void;
  onViewAllHistory: () => void;
  onViewAllProperties: () => void;
}

export function HomeScreenWeb({
  userName,
  userEmail,
  totalProperties,
  totalPurchasePrice,
  overduePaymentCards,
  upcomingDuePaymentCards,
  paymentUpdates,
  homeCoverImage,
  ventures,
  profileLoading,
  unitInstallmentsLoading,
  onPaymentCardPress,
  onPaymentUpdatePress,
  onVenturePress,
  onViewAllMissed,
  onViewAllDueSoon,
  onViewAllHistory,
  onViewAllProperties,
}: HomeScreenWebProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const isMobile = isSmallScreen();
  const { width: screenWidth } = useWindowDimensions();

  // Dynamic venture card sizing - evenly distribute edge to edge
  const [ventureGridWidth, setVentureGridWidth] = React.useState<number | null>(
    null,
  );

  // Use state for greeting to ensure it matches client's local system time
  // and updates dynamically if the page is left open.
  // Value is Contentful words only, e.g. "Good Evening" — comma is added
  // next to that text when a name follows.
  const [greetingPrefix, setGreetingPrefix] = React.useState<string>(() =>
    resolveGreetingPrefix(t),
  );

  React.useEffect(() => {
    setGreetingPrefix(resolveGreetingPrefix(t));

    const intervalId = setInterval(() => {
      setGreetingPrefix(resolveGreetingPrefix(t));
    }, 60000);

    return () => clearInterval(intervalId);
  }, [t]);

  // Helper function to get singular/plural payment label based on count
  const getPaymentLabel = (count: number) => {
    return count === 1 ? t("payments.tabLabel") : t("payments.title" as any);
  };

  // Helper function to format payment count display - Arabic format: "الدفعات : 3"
  const formatPaymentCount = (count: number) => {
    if (isRTL) {
      return `الدفعات : ${count}`;
    }
    return `${count} ${getPaymentLabel(count)}`;
  };

  // Show skeleton loading for initial profile load
  if (profileLoading) {
    return (
      <WebAppLayout
        activeTab="home"
        headerTitle=""
        breadcrumbs={[]}
        showBackButton={false}
      >
        <View
          style={[
            styles.container,
            { backgroundColor: colors.backgroundLevel2 },
          ]}
        >
          {/* Header Skeleton */}
          <View
            style={[
              styles.header,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <View style={styles.headerTop}>
              <View style={styles.headerLeft}>
                <Skeleton width={120} height={32} />
                <Skeleton width={80} height={32} />
              </View>
              <View style={styles.headerRight}>
                <Skeleton width={48} height={48} variant="circular" />
                <Skeleton width={180} height={48} borderRadius={12} />
              </View>
            </View>
          </View>

          {/* Main Content Skeleton */}
          <View style={styles.mainContent}>
            <ScrollView
              style={styles.contentArea}
              contentContainerStyle={styles.contentScroll}
            >
              {/* Hero Section Skeleton */}
              <View style={styles.heroSection}>
                <Skeleton width="100%" height={400} borderRadius={0} />
              </View>

              {/* Payment Sections Skeleton - Three Columns */}
              <View
                style={[
                  styles.paymentSections,
                ]}
              >
                {[1, 2, 3].map((colIndex) => (
                  <View
                    key={colIndex}
                    style={[
                      styles.paymentColumn,
                      { backgroundColor: colors.backgroundLevel1 },
                    ]}
                  >
                    <View
                      style={[
                        styles.paymentColumnHeader,
                      ]}
                    >
                      <Skeleton width={24} height={24} variant="circular" />
                      <Skeleton width={120} height={20} />
                    </View>
                    <View style={styles.paymentCardsList}>
                      {Array.from({ length: isMobile ? 2 : 3 }).map((_, i) => (
                        <View
                          key={i}
                          style={[
                            styles.paymentCard,
                            { borderColor: colors.outlineInactive },
                          ]}
                        >
                          <View style={styles.paymentCardContent}>
                            <Skeleton width={150} height={16} />
                            <View style={styles.paymentCardAmount}>
                              <Skeleton
                                width={24}
                                height={24}
                                variant="circular"
                              />
                              <Skeleton
                                width={100}
                                height={20}
                                style={{ marginLeft: 8 }}
                              />
                            </View>
                            <Skeleton width={100} height={14} />
                          </View>
                          <Skeleton width={18} height={18} variant="circular" />
                        </View>
                      ))}
                    </View>
                  </View>
                ))}
              </View>
            </ScrollView>
          </View>
        </View>
      </WebAppLayout>
    );
  }

  // Dynamic venture card sizing - evenly distribute edge to edge
  const figmaCardHeight = 206;
  const figmaCardWidth = 280;
  const figmaGap = 24;

  // Calculate available width (prefer actual grid width for accurate edge-to-edge sizing)
  const contentPadding = isMobile ? 16 : 0;
  const availableWidth = ventureGridWidth ?? screenWidth - contentPadding * 2;

  // Calculate how many cards fit per row based on minimum card width
  // Mobile: 2 columns, Desktop: dynamic based on available space
  const minCardWidth = isMobile ? 130 : 200; // Minimum card width before wrapping
  const maxColumns = Math.floor(
    (availableWidth + figmaGap) / (minCardWidth + figmaGap),
  );
  const actualColumns = Math.max(1, isMobile ? 2 : Math.min(maxColumns, 5)); // Max 5 on desktop

  // Calculate card width to evenly fill available space edge to edge
  const totalGapWidth = figmaGap * (actualColumns - 1);
  const ventureCardWidth = Math.floor(
    (availableWidth - totalGapWidth) / actualColumns,
  );
  const ventureCardHeight = Math.round(
    ventureCardWidth * (figmaCardHeight / figmaCardWidth),
  );

  // Render payment sections in correct order based on language
  const renderPaymentSections = () => {
    const missedSection =
      (unitInstallmentsLoading && overduePaymentCards.length === 0) ||
      overduePaymentCards.length > 0 ? (
        unitInstallmentsLoading && overduePaymentCards.length === 0 ? (
          <View
            key="missed-skeleton"
            style={[
              styles.paymentColumn,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <View
              style={[
                styles.paymentColumnHeader,
                {
                  flexDirection: isRTL ? "row-reverse" : "row",
                  alignSelf: isRTL ? "flex-start" : "flex-start",
                  alignItems: isRTL ? "flex-start" : "flex-start",
                },
              ]}
            >
              {isRTL ? (
                <>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 2 }}
                  >
                    {t("home.missedPayments")}
                  </Typography>
                  <View style={styles.iconContainer}>
                    <CircleAlertIcon size={24} color={colors.fontErrorText} />
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.iconContainer}>
                    <CircleAlertIcon size={24} color={colors.fontErrorText} />
                  </View>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("home.missedPayments")}
                  </Typography>
                </>
              )}
            </View>
            <View style={styles.paymentCardsList}>
              {Array.from({ length: isMobile ? 2 : 3 }).map((_, i) => (
                <View
                  key={i}
                  style={[
                    styles.paymentCard,
                    { borderColor: colors.outlineInactive },
                  ]}
                >
                  <PaymentCardSkeleton />
                </View>
              ))}
            </View>
          </View>
        ) : (
          <View
            key="missed"
            style={[
              styles.paymentColumn,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <View
              style={[
                styles.paymentColumnHeader,
                {
                  flexDirection: isRTL ? "row-reverse" : "row",
                  alignSelf: isRTL ? "flex-start" : "flex-start",
                  alignItems: isRTL ? "flex-start" : "flex-start",
                },
              ]}
            >
              {isRTL ? (
                <>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("home.missedPayments")}
                  </Typography>
                  <View style={styles.iconContainer}>
                    <CircleAlertIcon size={24} color={colors.fontErrorText} />
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.iconContainer}>
                    <CircleAlertIcon size={24} color={colors.fontErrorText} />
                  </View>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("home.missedPayments")}
                  </Typography>
                </>
              )}
            </View>
            <View style={styles.paymentCardsList}>
              {overduePaymentCards
                .slice(0, isMobile ? 2 : 3)
                .map(
                  (card: {
                    id: string;
                    propertyName: string;
                    amount: number;
                    paymentCount: number;
                  }) => (
                    <TouchableOpacity
                      key={card.id}
                      style={[
                        styles.paymentCard,
                        { borderColor: colors.outlineActive },
                      ]}
                      onPress={() => onPaymentCardPress(card.id, "overdue")}
                    >
                      <View style={styles.paymentCardContent}>
                        <Typography
                          variant="body"
                          color="tertiary"
                          style={[
                            styles.paymentCardProperty,
                            { textAlign: isRTL ? "right" : "left" },
                          ]}
                        >
                          {card.propertyName}
                        </Typography>
                        <View
                          style={[
                            styles.paymentCardAmount,
                            {
                              direction: "ltr",
                              justifyContent: isRTL ? "flex-end" : "flex-start",
                            },
                          ]}
                        >
                          <View style={{ paddingTop: 2 }}>
                            <DirhamIcon size={20} color={colors.fontPrimary} />
                          </View>
                          <Typography variant="title2" color="primary">
                            {card.amount.toLocaleString()}
                          </Typography>
                        </View>
                        <Typography
                          variant="body"
                          color="primary"
                          style={{ textAlign: isRTL ? "right" : "left" }}
                        >
                          {formatPaymentCount(card.paymentCount)}
                        </Typography>
                      </View>
                      <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                        <ChevronRightIcon
                          size={18}
                          color={colors.buttonDefault}
                        />
                      </View>
                    </TouchableOpacity>
                  ),
                )}
            </View>
            <Button
              title={t("propertyDetails.viewAll" as any)}
              variant="outline"
              onPress={onViewAllMissed}
              fullWidth
              style={styles.viewAllButton}
            />
          </View>
        )
      ) : null;

    const dueSoonSection =
      (unitInstallmentsLoading && upcomingDuePaymentCards.length === 0) ||
      upcomingDuePaymentCards.length > 0 ? (
        unitInstallmentsLoading && upcomingDuePaymentCards.length === 0 ? (
          <View
            key="due-soon-skeleton"
            style={[
              styles.paymentColumn,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <View
              style={[
                styles.paymentColumnHeader,
                {
                  flexDirection: isRTL ? "row-reverse" : "row",
                  alignSelf: isRTL ? "flex-start" : "flex-start",
                  alignItems: isRTL ? "flex-start" : "flex-start",
                },
              ]}
            >
              {isRTL ? (
                <>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 2 }}
                  >
                    {t("payments.dueSoonPayments")}
                  </Typography>
                  <View style={styles.iconContainer}>
                    <ClockIcon size={24} color={UI_PRIMITIVES.yellowDark} />
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.iconContainer}>
                    <ClockIcon size={24} color={UI_PRIMITIVES.yellowDark} />
                  </View>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("payments.dueSoonPayments")}
                  </Typography>
                </>
              )}
            </View>
            <View style={styles.paymentCardsList}>
              {Array.from({ length: isMobile ? 2 : 3 }).map((_, i) => (
                <View
                  key={i}
                  style={[
                    styles.paymentCard,
                    { borderColor: colors.outlineInactive },
                  ]}
                >
                  <PaymentCardSkeleton />
                </View>
              ))}
            </View>
          </View>
        ) : (
          <View
            key="due-soon"
            style={[
              styles.paymentColumn,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <View
              style={[
                styles.paymentColumnHeader,
                {
                  flexDirection: isRTL ? "row-reverse" : "row",
                  alignSelf: isRTL ? "flex-start" : "flex-start",
                  alignItems: isRTL ? "flex-start" : "flex-start",
                },
              ]}
            >
              {isRTL ? (
                <>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("payments.dueSoonPayments")}
                  </Typography>
                  <View style={styles.iconContainer}>
                    <ClockIcon size={24} color={UI_PRIMITIVES.yellowDark} />
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.iconContainer}>
                    <ClockIcon size={24} color={UI_PRIMITIVES.yellowDark} />
                  </View>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("payments.dueSoonPayments")}
                  </Typography>
                </>
              )}
            </View>
            <View style={styles.paymentCardsList}>
              {upcomingDuePaymentCards
                .slice(0, isMobile ? 2 : 3)
                .map(
                  (card: {
                    id: string;
                    propertyName: string;
                    amount: number;
                    paymentCount: number;
                  }) => (
                    <TouchableOpacity
                      key={card.id}
                      style={[
                        styles.paymentCard,
                        { borderColor: colors.outlineActive },
                      ]}
                      onPress={() => onPaymentCardPress(card.id, "upcomingDue")}
                    >
                      <View style={styles.paymentCardContent}>
                        <Typography
                          variant="body"
                          color="tertiary"
                          style={[
                            styles.paymentCardProperty,
                            { textAlign: isRTL ? "right" : "left" },
                          ]}
                        >
                          {card.propertyName}
                        </Typography>
                        <View
                          style={[
                            styles.paymentCardAmount,
                            {
                              direction: "ltr",
                              justifyContent: isRTL ? "flex-end" : "flex-start",
                            },
                          ]}
                        >
                          <View style={{ paddingTop: 2 }}>
                            <DirhamIcon size={20} color={colors.fontPrimary} />
                          </View>
                          <Typography variant="title2" color="primary">
                            {card.amount.toLocaleString()}
                          </Typography>
                        </View>
                        <Typography
                          variant="body"
                          color="primary"
                          style={{ textAlign: isRTL ? "right" : "left" }}
                        >
                          {formatPaymentCount(card.paymentCount)}
                        </Typography>
                      </View>
                      <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                        <ChevronRightIcon
                          size={18}
                          color={colors.buttonDefault}
                        />
                      </View>
                    </TouchableOpacity>
                  ),
                )}
            </View>
            <Button
              title={t("propertyDetails.viewAll" as any)}
              variant="outline"
              onPress={onViewAllDueSoon}
              fullWidth
              style={styles.viewAllButton}
            />
          </View>
        )
      ) : null;

    const historySection =
      (unitInstallmentsLoading && paymentUpdates.length === 0) ||
      paymentUpdates.length > 0 ? (
        unitInstallmentsLoading && paymentUpdates.length === 0 ? (
          <View
            key="history-skeleton"
            style={[
              styles.paymentColumn,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <View
              style={[
                styles.paymentColumnHeader,
                {
                  flexDirection: isRTL ? "row-reverse" : "row",
                  alignSelf: isRTL ? "flex-start" : "flex-start",
                  alignItems: isRTL ? "flex-start" : "flex-start",
                },
              ]}
            >
              {isRTL ? (
                <>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 2 }}
                  >
                    {t("payments.history")}
                  </Typography>
                  <View style={styles.iconContainer}>
                    <PaymentHistoryIcon size={24} color="#4CAF50" />
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.iconContainer}>
                    <PaymentHistoryIcon size={24} color="#4CAF50" />
                  </View>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("payments.history")}
                  </Typography>
                </>
              )}
            </View>
            <View style={styles.paymentCardsList}>
              {Array.from({ length: isMobile ? 2 : 3 }).map((_, i) => (
                <Card
                  key={i}
                  variant="default"
                  style={[
                    styles.historyCard,
                    {
                      borderColor: colors.outlineInactive,
                      backgroundColor: colors.backgroundLevel1,
                    },
                  ]}
                >
                  <View style={styles.historyCardContent}>
                    <Skeleton width={120} height={12} />
                    <View style={styles.historyCardRow}>
                      <Skeleton width={180} height={16} />
                      <View style={styles.historyCardAmount}>
                        <Skeleton width={16} height={16} variant="circular" />
                        <Skeleton
                          width={80}
                          height={16}
                          style={{ marginLeft: 4 }}
                        />
                      </View>
                    </View>
                    <View style={styles.historyCardStatus}>
                      <Skeleton width={100} height={14} />
                      <Skeleton width={60} height={20} />
                    </View>
                  </View>
                </Card>
              ))}
            </View>
          </View>
        ) : (
          <View
            key="history"
            style={[
              styles.paymentColumn,
              { backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <View
              style={[
                styles.paymentColumnHeader,
                {
                  flexDirection: isRTL ? "row-reverse" : "row",
                  alignSelf: isRTL ? "flex-start" : "flex-start",
                  alignItems: isRTL ? "flex-start" : "flex-start",
                },
              ]}
            >
              {isRTL ? (
                <>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("payments.history")}
                  </Typography>
                  <View style={styles.iconContainer}>
                    <PaymentHistoryIcon size={24} color="#4CAF50" />
                  </View>
                </>
              ) : (
                <>
                  <View style={styles.iconContainer}>
                    <PaymentHistoryIcon size={24} color="#4CAF50" />
                  </View>
                  <Typography
                    variant="title3"
                    color="primary"
                    style={{ paddingTop: 1 }}
                  >
                    {t("payments.history")}
                  </Typography>
                </>
              )}
            </View>
            <View style={styles.paymentCardsList}>
              {paymentUpdates
                .slice(0, isMobile ? 2 : 3)
                .map(
                  (update: {
                    id: string;
                    propertyName: string;
                    paymentTitle: string;
                    amount: number;
                    status: "in-process" | "confirmed";
                    paymentDate: string;
                  }) => (
                    <TouchableOpacity
                      key={update.id}
                      style={[
                        styles.historyCard,
                        { borderColor: colors.outlineActive },
                      ]}
                      onPress={() => onPaymentUpdatePress(update.id)}
                    >
                      <View style={styles.historyCardContent}>
                        <Typography
                          variant="body"
                          color="tertiary"
                          style={{ textAlign: isRTL ? "right" : "left" }}
                        >
                          {update.propertyName}
                        </Typography>
                        <View style={styles.historyCardRow}>
                          <Typography
                            variant="headline"
                            color="primary"
                            style={[
                              styles.historyCardTitle,
                              { textAlign: isRTL ? "right" : "left" },
                            ]}
                          >
                            {update.paymentTitle}
                          </Typography>
                          <View
                            style={[
                              styles.historyCardAmount,
                              { direction: "ltr" },
                            ]}
                          >
                            <View style={{ paddingTop: 2 }}>
                              <DirhamIcon
                                size={16.5}
                                color={colors.fontPrimary}
                              />
                            </View>
                            <Typography
                              variant="headline"
                              color="primary"
                              style={{ paddingTop: 0 }}
                            >
                              {(update.amount ?? 0).toLocaleString()}
                            </Typography>
                          </View>
                          <View
                            style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}
                          >
                            <ChevronRightIcon
                              size={16}
                              color={colors.buttonDefault}
                            />
                          </View>
                        </View>
                        <View style={styles.historyCardStatus}>
                          <Typography
                            variant="body"
                            color="secondary"
                            style={{
                              textAlign: isRTL ? "right" : "left",
                              flex: 1,
                            }}
                          >
                            {update.paymentDate
                              ? `Paid on ${update.paymentDate}`
                              : t("payments.lastPayment")}
                          </Typography>
                          <StatusChip status="paid" size="small" />
                        </View>
                      </View>
                    </TouchableOpacity>
                  ),
                )}
            </View>
            <Button
              title={t("propertyDetails.viewAll" as any)}
              variant="outline"
              onPress={onViewAllHistory}
              fullWidth
              style={styles.viewAllButton}
            />
          </View>
        )
      ) : null;

    // Return sections in correct order: LTR = Missed, Due Soon, History | RTL = History, Due Soon, Missed
    if (isRTL) {
      return [historySection, dueSoonSection, missedSection].filter(Boolean);
    }
    return [missedSection, dueSoonSection, historySection].filter(Boolean);
  };

  return (
    <WebAppLayout
      activeTab="home"
      headerTitle=""
      breadcrumbs={[]}
      showBackButton={false}
    >
      <View
        style={[styles.container, { backgroundColor: colors.backgroundLevel2 }]}
      >
        {/* Main Content */}
        <View style={styles.mainContent}>
          {/* Content Area */}
          <ScrollView
            style={styles.contentArea}
            contentContainerStyle={styles.contentScroll}
            showsVerticalScrollIndicator={false}
          >
            {/* Hero Section - First */}
            <View style={styles.heroSection}>
              {homeCoverImage && (
                <View style={styles.heroImageContainer}>
                  <Image
                    source={{ uri: homeCoverImage }}
                    style={styles.heroImage}
                    contentFit="cover"
                  />
                  <LinearGradient
                    // Match Figma: bottom dark to top transparent, overall ~50% opacity
                    colors={[
                      "rgba(0, 0, 0, 0)", // from #000 with 50% opacity at the bottom
                      "rgba(53, 53, 53, 0.396)", // via rgba(53,53,53,0.792) with 50% opacity
                      "rgba(0, 0, 0, 0.5)", // to transparent at the top
                    ]}
                    locations={[0, 0.42119, 1]}
                    style={styles.heroGradient}
                  />
                  <View style={styles.heroContent}>
                    <Typography
                      variant="largeTitle"
                      color="white"
                      style={styles.heroGreeting}
                    >
                      {withGreetingNameComma(greetingPrefix, userName)} {userName}
                      {!isRTL ? "!" : ""}
                    </Typography>
                    <View style={styles.heroStats}>
                      <TouchableOpacity
                        style={styles.heroStatItem}
                        onPress={onViewAllProperties}
                        activeOpacity={0.8}
                        accessibilityRole="button"
                        accessibilityLabel={t("home.totalProperties")}
                      >
                        <Typography variant="body" color="white">
                          {t("home.totalProperties")}
                        </Typography>
                        <Typography variant="title2" color="white">
                          {totalProperties}
                        </Typography>
                      </TouchableOpacity>
                      <View style={styles.heroStatItem}>
                        <Typography variant="body" color="white">
                          {t("home.purchasePrice" as any)}
                        </Typography>
                        <View
                          style={[styles.heroStatAmount, { direction: "ltr" }]}
                        >
                          <DirhamIcon size={20} color="white" />
                          <Typography
                            variant="title2"
                            color="white"
                            style={{ textAlign: "left" }}
                          >
                            {totalPurchasePrice.toLocaleString()}
                          </Typography>
                        </View>
                      </View>
                    </View>
                  </View>
                </View>
              )}
            </View>

            {/* Payment Sections */}
            <View
              style={[
                styles.paymentSections,
                // Stack columns vertically on mobile web
                isMobile && { flexDirection: "column" },
                // On desktop RTL, mirror the row so the first item appears on the right
                !isMobile && isRTL && { flexDirection: "row-reverse" },
              ]}
            >
              {renderPaymentSections()}
            </View>

            {/* Ventures Section */}
            <View style={styles.bloomNewsSection} nativeID="home-bloom-ventures-section">
              <Typography
                variant="title3"
                color="primary"
                style={[
                  styles.bloomNewsTitle,
                  {
                    textAlign: isRTL ? "right" : "left",
                    ...(isRTL ? { paddingRight: 10 } : {}),
                  },
                ]}
              >
                {t("home.bloomVentures")}
              </Typography>
              <View
                style={styles.venturesGrid}
                onLayout={(event) =>
                  setVentureGridWidth(event.nativeEvent.layout.width)
                }
              >
                {ventures.map((venture: { id: string; logoUrl: string }) => (
                  <TouchableOpacity
                    key={venture.id}
                    style={[
                      styles.ventureCard,
                      {
                        width: ventureCardWidth,
                        height: ventureCardHeight,
                      },
                    ]}
                    onPress={() => onVenturePress(venture.id)}
                  >
                    {venture.logoUrl && (
                      <Image
                        source={{ uri: venture.logoUrl }}
                        style={styles.ventureImage}
                        contentFit="contain"
                        contentPosition="center"
                      />
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>
        </View>
      </View>
    </WebAppLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 32,
  },
  headerTop: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 49,
  },
  headerTitle: {
    width: 235,
  },
  headerRight: {
    flexDirection: "row",
    gap: 24,
    alignItems: "center",
  },
  notificationButton: {
    width: 56,
    height: 56,
    borderRadius: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  profileButton: {
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  profileContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    borderWidth: 1,
  },
  profileInfo: {
    width: 168,
  },
  mainContent: {
    flex: 1,
  },
  contentArea: {
    flex: 1,
  },
  contentScroll: {
    gap: 24,
  },
  heroSection: {
    height: 352,
    borderRadius: 12,
    overflow: "hidden",
  },
  heroImageContainer: {
    width: "100%",
    height: "100%",
    position: "relative",
  },
  heroImage: {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  heroGradient: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  heroContent: {
    flex: 1,
    justifyContent: "flex-end",
    padding: 16,
    gap: 9,
    alignItems: "center",
  },
  heroGreeting: {
    textAlign: "center",
    maxWidth: 393, // Per Figma: constrained title width
    alignSelf: "center",
  },
  heroStats: {
    flexDirection: "row",
    gap: 12,
    justifyContent: "center",
    alignItems: "stretch",
    width: "100%",
    maxWidth: 757, // Per Figma: stats container width
    height: 91,
    borderRadius: 12,
    alignSelf: "center",
  },
  heroStatItem: {
    flex: 1,
    alignItems: "center",
    gap: 4,
    paddingVertical: 20,
    paddingHorizontal: 4,
  },
  heroStatAmount: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  paymentSections: {
    flexDirection: "row",
    gap: 24,
  },
  paymentColumn: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
    gap: 8,
    flexDirection: "column",
  },
  paymentColumnHeader: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 4,
    paddingBottom: 8,
  },
  iconContainer: {
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  paymentCardsList: {
    flex: 1,
    gap: 12,
    justifyContent: "flex-start",
  },
  paymentCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    borderWidth: 1,
    borderRadius: 12,
    padding: 16, // Per Figma: Spacing/4 (16px) instead of 12px
    gap: 16,
  },
  paymentCardContent: {
    flex: 1,
    gap: 8,
  },
  paymentCardProperty: {
    width: "100%",
  },
  paymentCardAmount: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  viewAllButton: {
    marginTop: "auto", // push to bottom of section
    height: 48,
  },
  historyCard: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 16, // Per Figma: Spacing/4 (16px) instead of 12px
    gap: 8,
  },
  historyCardContent: {
    gap: 8,
  },
  historyCardRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  historyCardTitle: {
    flex: 1,
  },
  historyCardAmount: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  historyCardStatus: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
  },
  bloomNewsSection: {
    gap: 8,
  },
  bloomNewsTitle: {
    marginBottom: 8,
    marginTop: 54,
  },
  venturesGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 24,
    rowGap: 24,
    justifyContent: "space-between",
  },
  ventureCard: {
    borderRadius: 12,
    overflow: "hidden",
  },
  ventureImage: {
    width: "100%",
    height: "100%",
  },
});
